import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "flex h-full flex-col gap-2" }
const _hoisted_2 = { class: "flex min-h-0 flex-1 flex-col rounded border" }
const _hoisted_3 = { class: "flex flex-col items-center gap-2" }

import type {
        ColumnDef,
        SortingState,
        ColumnFiltersState,
        RowSelectionState,
    } from '@tanstack/vue-table';
    import {
        FlexRender,
        getCoreRowModel,
        useVueTable,
        getPaginationRowModel,
        getSortedRowModel,
        getFilteredRowModel,
    } from '@tanstack/vue-table';
    import { ref } from 'vue';

    import {
        Table,
        TableBody,
        TableCell,
        TableHead,
        TableHeader,
        TableRow,
    } from '@/components/ui/table';

    import DebouncedInput from '@/components/DebouncedInput.vue';
    import { valueUpdater } from '@/lib/utils';
    import Icon from '@/components/Icon.vue';
    import Loading from '@/components/Loading.vue';
    import {
        booleanFilterFn,
        valueInArrayFilterFn,
        dateRangeFilterFn,
        periodRangeFilterFn,
    } from '@/utils/filterFunctions';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'LogFilterTable',
  props: {
    columns: {},
    data: {},
    loading: { type: Boolean },
    isError: { type: Boolean },
    defaultSorting: { default: () => [{ id: 'count', desc: true }] },
    defaultColumnFilters: { default: () => [] }
  },
  emits: ["row-selection-change"],
  setup(__props: any, { emit: __emit }) {

    const props = __props;

    const sorting = ref<SortingState>(props.defaultSorting);
    const columnFilters = ref<ColumnFiltersState>(props.defaultColumnFilters);
    const rowSelection = ref<RowSelectionState>({});
    const globalFilter = ref('');

    const emit = __emit;

    const table = useVueTable({
        get data() {
            return props.data;
        },
        get columns() {
            return props.columns;
        },
        get rowCount() {
            return props.data.length;
        },

        manualPagination: true,
        enableRowSelection: true,
        enableMultiRowSelection: false,
        filterFns: {
            boolean: booleanFilterFn,
            valueInArray: valueInArrayFilterFn,
            dateRange: dateRangeFilterFn,
            periodRange: periodRangeFilterFn,
        },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getRowId: (row) => row.name,
        onSortingChange: (updaterOrValue) => valueUpdater(updaterOrValue, sorting),
        onColumnFiltersChange: (updaterOrValue) => valueUpdater(updaterOrValue, columnFilters),
        onRowSelectionChange: (updaterOrValue) => {
            valueUpdater(updaterOrValue, rowSelection);
            const selectedRows = table.getFilteredSelectedRowModel().rows;
            const selectedValues = selectedRows.map((row: any) => row.original.name);
            emit('row-selection-change', selectedValues);
        },
        onGlobalFilterChange: (updaterOrValue) => valueUpdater(updaterOrValue, globalFilter),
        globalFilterFn: (row, columnId, filterValue) => {
            const search = String(filterValue).toLowerCase();
            let currentRow = row;
            while (currentRow.parentId) {
                const parentRow = table.getRow(currentRow.parentId);
                if (String(parentRow.getValue(columnId)).toLowerCase().includes(search)) {
                    return true;
                }
                currentRow = parentRow;
            }
            return String(row.getValue(columnId)).toLowerCase().includes(search);
        },

        state: {
            get sorting() {
                return sorting.value;
            },
            get columnFilters() {
                return columnFilters.value;
            },
            get rowSelection() {
                return rowSelection.value;
            },
            get globalFilter() {
                return globalFilter.value;
            },
        },
    });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(DebouncedInput, {
      placeholder: _ctx.$t('datatables.search_placeholder'),
      modelValue: globalFilter.value ?? '',
      "onUpdate:modelValue": _cache[0] || (_cache[0] = (value) => (globalFilter.value = String(value))),
      debounce: 200,
      class: "min-h-7.5"
    }, null, 8, ["placeholder", "modelValue"]),
    _createElementVNode("div", _hoisted_2, [
      (!_unref(table).getRowModel().rows?.length && _ctx.loading)
        ? (_openBlock(), _createBlock(Loading, { key: 0 }))
        : (_openBlock(), _createBlock(_unref(Table), { key: 1 }, {
            default: _withCtx(() => [
              _createVNode(_unref(TableHeader), null, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(table).getHeaderGroups(), (headerGroup) => {
                    return (_openBlock(), _createBlock(_unref(TableRow), {
                      key: headerGroup.id
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerGroup.headers, (header) => {
                          return (_openBlock(), _createBlock(_unref(TableHead), {
                            key: header.id,
                            style: _normalizeStyle(
                                header.column.getIsPinned() === 'left' && {
                                    left: `${header.column.getStart('left')}px`,
                                }
                            )
                          }, {
                            default: _withCtx(() => [
                              (!header.isPlaceholder)
                                ? (_openBlock(), _createBlock(_unref(FlexRender), {
                                    key: 0,
                                    render: header.column.columnDef.header,
                                    props: header.getContext()
                                  }, null, 8, ["render", "props"]))
                                : _createCommentVNode("", true)
                            ]),
                            _: 2
                          }, 1032, ["style"]))
                        }), 128))
                      ]),
                      _: 2
                    }, 1024))
                  }), 128))
                ]),
                _: 1
              }),
              _createVNode(_unref(TableBody), null, {
                default: _withCtx(() => [
                  (_unref(table).getRowModel().rows?.length)
                    ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(_unref(table).getRowModel().rows, (row) => {
                        return (_openBlock(), _createBlock(_unref(TableRow), {
                          key: row.id,
                          "data-state": row.getIsSelected() ? 'selected' : undefined
                        }, {
                          default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.getVisibleCells(), (cell) => {
                              return (_openBlock(), _createBlock(_unref(TableCell), {
                                key: cell.id,
                                style: _normalizeStyle(
                                        cell.column.getIsPinned() === 'left' && {
                                            left: `${cell.column.getStart('left')}px`,
                                        }
                                    )
                              }, {
                                default: _withCtx(() => [
                                  (cell.getIsAggregated())
                                    ? (_openBlock(), _createBlock(_unref(FlexRender), {
                                        key: 0,
                                        render: cell.column.columnDef.cell,
                                        props: cell.getContext()
                                      }, null, 8, ["render", "props"]))
                                    : (_openBlock(), _createBlock(_unref(FlexRender), {
                                        key: 1,
                                        render: cell.column.columnDef.cell,
                                        props: cell.getContext()
                                      }, null, 8, ["render", "props"]))
                                ]),
                                _: 2
                              }, 1032, ["style"]))
                            }), 128))
                          ]),
                          _: 2
                        }, 1032, ["data-state"]))
                      }), 128))
                    : (_openBlock(), _createBlock(_unref(TableRow), { key: 1 }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(TableCell), {
                            colspan: _ctx.columns.length,
                            class: "h-24 text-center"
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_3, [
                                (_ctx.isError)
                                  ? (_openBlock(), _createBlock(Icon, {
                                      key: 0,
                                      name: "alertCircle",
                                      class: "text-destructive size-6"
                                    }))
                                  : _createCommentVNode("", true),
                                _createElementVNode("span", null, _toDisplayString(_ctx.$t('datatables.infoEmpty')), 1)
                              ])
                            ]),
                            _: 1
                          }, 8, ["colspan"])
                        ]),
                        _: 1
                      }))
                ]),
                _: 1
              })
            ]),
            _: 1
          }))
    ])
  ]))
}
}

})